import { useQuery } from '@apollo/react-hooks';
import { Popover } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import React, { CSSProperties, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View, ViewStyle } from 'react-native';
import { FixedSizeList } from 'react-window';

import { Checkbox } from '../../../../core-ui';
import { AllCities } from '../../../../generated/AllCities';
import { GET_ALL_CITIES } from '../../../../graphql/queries';
import { colors, spacing } from '../../constants/theme';
import { Button, Text, TextInput } from '../../core-ui';

// NOTE: fixed row height/viewport for react-window — the list is virtualized since it
// can hold hundreds of cities, and mounting all of them at once visibly delays the popover.
const ROW_HEIGHT = 48;
const LIST_HEIGHT = 260;

type Props<T extends string | number> = {
  selectedValues: Array<T>;
  onApply: (values: Array<T>) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placeholder?: string;
  label?: string;
  allLabel?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  containerStyle?: ViewStyle;
};

// NOTE: like CheckboxDropdown, this panel is built around the city list, kept generic over T for type-safety at the call site.
const SearchableCheckboxDropdown = <T extends string | number>(
  props: Props<T>,
) => {
  const {
    selectedValues,
    onApply,
    open,
    onOpenChange,
    placeholder = 'Semua',
    label,
    allLabel = 'Semua',
    searchPlaceholder = 'Cari',
    disabled,
    containerStyle,
  } = props;

  // NOTE: cache-first so reopening (or the prefetch in TokoVerification) never refetches the list
  let { data, loading } = useQuery<AllCities>(GET_ALL_CITIES, {
    fetchPolicy: 'cache-first',
  });

  let options = (data?.cities ?? []).map((city) => ({
    label: city.name,
    value: (city.id as unknown) as T,
  }));

  // NOTE: an empty applied selection means "no filter", which is equivalent to "all cities" —
  // so it should be staged/displayed as everything checked rather than nothing checked.
  const allValues = options.map((option) => option.value);
  const getDefaultStagedValues = (values: Array<T>) =>
    values.length > 0 ? values : allValues;

  const anchorRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState('');
  // NOTE: null means "not staged yet this session" and falls back live to the current
  // options/selectedValues — so opening while the city list is still loading (or reopening
  // after a fresh apply) always reflects the latest data instead of a stale snapshot.
  const [stagedValues, setStagedValues] = useState<Array<T> | null>(null);
  const effectiveStagedValues =
    stagedValues ?? getDefaultStagedValues(selectedValues);

  const handleTriggerPress = () => {
    if (!open) {
      setStagedValues(null);
      setSearchText('');
    }
    onOpenChange(!open);
  };

  const toggleOne = (value: T) => {
    setStagedValues(
      effectiveStagedValues.includes(value)
        ? effectiveStagedValues.filter((v) => v !== value)
        : [...effectiveStagedValues, value],
    );
  };

  const isAllSelected =
    options.length > 0 && effectiveStagedValues.length === options.length;

  const toggleAll = () => {
    setStagedValues(isAllSelected ? [] : allValues);
  };

  const handleReset = () => {
    setStagedValues(null);
    onApply([]);
    onOpenChange(false);
  };

  const handleApply = () => {
    // NOTE: everything selected is equivalent to no filter, so it round-trips back to "Semua"
    onApply(isAllSelected ? [] : effectiveStagedValues);
    setStagedValues(null);
    onOpenChange(false);
  };

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchText.trim().toLowerCase()),
  );

  let isChosen = selectedValues.length !== 0;
  const displayText = isChosen
    ? `${selectedValues.length} Kota/Kab dipilih`
    : placeholder;

  return (
    <View style={[styles.wrapper, containerStyle]}>
      {!!label && (
        <Text size="xs" style={styles.label}>
          {label}
        </Text>
      )}

      {/* Trigger — not gated on `loading`; the popover opens immediately and shows its own loading state */}
      <div ref={anchorRef}>
        <TouchableOpacity
          disabled={disabled}
          style={[styles.trigger, open && styles.triggerOpen]}
          onPress={handleTriggerPress}
        >
          <Text
            style={[
              styles.triggerText,
              {
                color: isChosen
                  ? colors.background.secondaryBlue
                  : colors.text.black,
              },
            ]}
            color={disabled ? 'grey' : 'black'}
            numberOfLines={1}
            bold={isChosen}
          >
            {displayText}
          </Text>
          <ExpandMoreIcon
            fontSize="small"
            htmlColor={disabled ? colors.text.grey : colors.text.black}
            style={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        </TouchableOpacity>
      </div>

      {/* Dropdown panel — renders in a portal, never clipped */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => onOpenChange(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        PaperProps={{
          style: {
            borderRadius: 4,
            border: `1px solid ${colors.background.blue}`,
            overflow: 'hidden',
          },
        }}
      >
        <View style={styles.panel}>
          <View style={styles.searchBox}>
            <TextInput
              value={searchText}
              onChangeText={setSearchText}
              placeholder={searchPlaceholder}
              leftNode={
                <SearchIcon fontSize="small" htmlColor={colors.text.grey} />
              }
              variant="standard"
              InputProps={{
                disableUnderline: true,
                style: { backgroundColor: 'transparent' },
              }}
              containerStyle={styles.searchInputContainer}
            />
          </View>

          {options.length > 0 && (
            <TouchableOpacity
              style={styles.optionRow}
              disabled={disabled}
              onPress={toggleAll}
            >
              <Text style={styles.optionLabel}>{allLabel}</Text>
              <Checkbox checked={isAllSelected} disabled={disabled} />
            </TouchableOpacity>
          )}
          {loading ? (
            <Text style={styles.emptyText}>Memuat data...</Text>
          ) : filteredOptions.length === 0 ? (
            <Text style={styles.emptyText}>Tidak ada data</Text>
          ) : (
            // NOTE: virtualized — only the rows visible in LIST_HEIGHT are ever mounted
            <FixedSizeList
              className="hide-scrollbar"
              height={LIST_HEIGHT}
              width="100%"
              itemCount={filteredOptions.length}
              itemSize={ROW_HEIGHT}
              itemData={filteredOptions}
            >
              {({
                index,
                style,
                data,
              }: {
                index: number;
                style: CSSProperties;
                data: Array<{ label: string; value: T }>;
              }) => {
                const option = data[index];
                return (
                  <TouchableOpacity
                    key={option.value}
                    style={[styles.optionRow, style as ViewStyle]}
                    disabled={disabled}
                    onPress={() => toggleOne(option.value)}
                  >
                    <Text style={styles.optionLabel}>{option.label}</Text>
                    <Checkbox
                      checked={effectiveStagedValues.includes(option.value)}
                      disabled={disabled}
                    />
                  </TouchableOpacity>
                );
              }}
            </FixedSizeList>
          )}

          <View style={styles.footer}>
            <Button
              title="Reset"
              preset="secondary"
              onPress={handleReset}
              disabled={disabled}
              style={styles.resetButton}
              titleTextProps={{ color: 'red' }}
            />
            <Button
              title={`Pilih (${effectiveStagedValues.length})`}
              preset="primary"
              onPress={handleApply}
              disabled={disabled}
            />
          </View>
        </View>
      </Popover>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {},
  label: {
    marginBottom: spacing.xxs,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: colors.border.primary,
    borderRadius: 2,
    paddingHorizontal: spacing.m,
    height: 40,
    backgroundColor: colors.background.white,
  },
  triggerOpen: {
    borderColor: colors.button.background.primary,
  },
  triggerText: {
    flex: 1,
    marginRight: spacing.xxs,
  },
  panel: {
    width: 320,
    paddingVertical: spacing.s,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.boxGrey,
    borderRadius: 6,
    marginHorizontal: spacing.m,
    marginBottom: spacing.xxs,
    paddingHorizontal: spacing.s,
  },
  searchInputContainer: {
    flex: 1,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: ROW_HEIGHT,
    paddingLeft: spacing.m,
  },
  optionLabel: {
    flex: 1,
    marginRight: spacing.s,
  },
  emptyText: {
    paddingHorizontal: spacing.m,
    paddingVertical: spacing.s,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: colors.border.primary,
    marginTop: spacing.xxs,
    paddingTop: spacing.s,
    paddingHorizontal: spacing.m,
  },
  resetButton: {
    borderColor: colors.text.red,
  },
});

export default SearchableCheckboxDropdown;
